import React, { useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import {
  borderRadius,
  color,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  opacity,
  position,
  space,
  textAlign,
  top
} from 'styled-system';
import { FiX } from 'react-icons/fi';
import flexHelper from './utils/flex';
import size from './utils/size';
import { textTransform } from './utils/typography';
import Icon from './Icon';
import Text from './Text';
import { guessByChildText } from '../util/ariaUtils';

export const TAG_SMALL_ROUND_PADDING = 7;

const StyledTag = styled.span`
  &.${Classes.TAG} {
    ${borderRadius};
    ${color};
    ${flexHelper};
    ${fontFamily};
    ${fontSize};
    ${fontWeight};
    ${lineHeight};
    ${size};
    ${space};
    ${textAlign};
    ${position};
    ${textTransform};
    ${top};
    ${opacity};
  }

  &.${Classes.MULTILINE} .${Classes.TAG_REMOVE} {
    align-self: flex-start;
  }

  ${({ onClick }) =>
    !!onClick &&
    css`
      cursor: pointer;
    `}
`;

/**
 * @param {object} props
 * @param {string} [props.bg]
 * @param {React.ReactNode} props.children
 * @param {object} [props.style]
 * @param {string} [props.color]
 * @param {'emergency' | 'alert' | 'critical' | 'error' | 'warning' | 'severe' | 'notice' | 'info' | 'debug' | 'success' | 'none' | 'danger'} [props.intent]
 *  not clear whether all of these are honored
 * @param {string} [props.icon]
 * @param {object} [props.iconProps]
 * @param {React.CSSProperties['fontWeight']} [props.fontWeight]
 * @param {string} [props.title]
 * @param {string | number} [props.mb]
 * @param {boolean} [props.round]
 * @param {boolean} [props.interactive]
 * @param {function} [props.onClick]
 * @returns {JSX.Element}
 */
function Tag({
  active,
  ariaLabel,
  children,
  circle,
  className,
  fill,
  icon,
  intent,
  interactive,
  large,
  minimal,
  monospace,
  multiline,
  rightIcon,
  round,
  small,
  onRemove,
  iconProps,
  ellipsis,
  muted,
  variant,
  ...rest
}) {
  const theme = useContext(ThemeContext) || {};
  const isDarkMode = theme.name === 'dark';
  const style = {};
  if (muted) {
    style.opacity = isDarkMode ? '65%' : '80%';
    style.bg = 'lightGray1';
    style.color = 'blackText';
  }

  const ariaLabelText = ariaLabel || guessByChildText(children);
  const ariaProps = {};
  if (ariaLabelText) {
    ariaProps['aria-label'] = ariaLabelText;
  }

  const handleRemoveKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onRemove(e);
    }
  };
  return (
    <StyledTag
      className={classNames(Classes.TAG, className, {
        [Classes.ACTIVE]: active,
        [Classes.FILL]: fill,
        [Classes.INTERACTIVE]: interactive,
        [Classes.intentClass(intent)]: intent,
        [Classes.LARGE]: large,
        [Classes.MINIMAL]: minimal,
        [Classes.MONOSPACE_TEXT]: monospace,
        [Classes.MULTILINE]: multiline,
        [Classes.ROUND]: round,
        [Classes.SMALL]: small,
        circle
      })}
      {...ariaProps}
      intent={intent}
      minimal={minimal}
      {...style}
      {...rest}
    >
      {icon && <Icon className="tag-icon" icon={icon} iconSize={small ? 12 : 14} mr="4px" {...iconProps} />}
      <Text
        className={classNames(Classes.FILL, {
          [Classes.TEXT_OVERFLOW_ELLIPSIS]: ellipsis
        })}
        small={small}
        monospace={monospace}
      >
        {children}
      </Text>
      {rightIcon && <Icon className="tag-icon" icon={rightIcon} iconSize={small ? 12 : 14} />}
      {onRemove && (
        <Icon
          className={Classes.TAG_REMOVE}
          aria-label={`Remove ${ariaLabelText}`}
          aria-hidden="false"
          tabIndex="0"
          icon={FiX}
          onClick={onRemove}
          onKeyDown={handleRemoveKeyDown}
        />
      )}
    </StyledTag>
  );
}

Tag.defaultProps = {
  justifyContent: 'center',
  // should in no circumstance be false!
  minimal: true,
  ellipsis: true
};

export default Tag;
